/**
 * Detalle de run de reporte con polling automático hasta completar/fallar.
 * Permite descargar resultado y generar share token público.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ReportShareDialog } from "@/components/feature/media/report-share-dialog";
import { media } from "@/lib/api";

export const Route = createFileRoute("/_auth/media/reports_/runs/$runId")({
  component: RunDetail,
});

function RunDetail() {
  const { runId } = Route.useParams();
  const id = Number(runId);

  const { data, isLoading } = useQuery({
    queryKey: ["media", "run", id],
    queryFn: () => media.getRun(id),
    // Polling: cada 2s si pending/running, parar al completar/fallar
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "pending" || status === "running" ? 2000 : false;
    },
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data) return <div>Run no encontrado</div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg">Ejecución #{data.id}</CardTitle>
          <StatusBadge status={data.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Iniciado</div>
            <div>{data.started_at ? new Date(data.started_at).toLocaleString("es-PE") : "—"}</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Terminado</div>
            <div>{data.finished_at ? new Date(data.finished_at).toLocaleString("es-PE") : "—"}</div>
          </div>
        </div>

        {data.status === "running" && (
          <div className="flex items-center gap-2 text-sm text-ink-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded">
            <Loader2 size={16} className="animate-spin" />
            Generando reporte... actualiza cada 2s.
          </div>
        )}

        {data.status === "failed" && data.error && (
          <div className="text-sm text-bad bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
            <strong>Error:</strong> {data.error}
          </div>
        )}

        {data.status === "completed" && data.file_url && (
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <a href={media.downloadRunUrl(data.id)} target="_blank" rel="noopener noreferrer">
                <Download size={14} /> Descargar
              </a>
            </Button>
            <ReportShareDialog run={data} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "completed") return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 size={12} /> Completado</Badge>;
  if (status === "failed") return <Badge className="bg-rose-100 text-rose-800"><XCircle size={12} /> Falló</Badge>;
  if (status === "running") return <Badge className="bg-amber-100 text-amber-800"><Loader2 size={12} className="animate-spin" /> Ejecutando</Badge>;
  return <Badge className="bg-ink-100 text-ink-700"><Clock size={12} /> Pendiente</Badge>;
}
